import React, { useEffect, useMemo, useRef, useState } from "react";
import KitchenMockup from "./KitchenMockup";
import WardrobeMockup from "./WardrobeMockup";
import LeadCaptureModal from "./LeadCaptureModal";
import { PresetGrid, KitchenControls, WardrobeControls } from "./ConfiguratorControls";
import {
  KITCHEN_SWATCHES,
  KITCHEN_PRESETS,
  WARDROBE_SWATCHES,
  WARDROBE_PRESETS,
} from "../data/designOptions";
import { Bookmark, Send, RefreshCw, GitCompareArrows, ChefHat, Shirt } from "lucide-react";

const INITIAL_KITCHEN = KITCHEN_PRESETS[0].config;
const INITIAL_WARDROBE = WARDROBE_PRESETS[0].config;

// ---------- Before / After overlay ----------
const BeforeAfter = ({ beforeChild, afterChild, position, onMove }) => (
  <div className="relative w-full select-none overflow-hidden" data-testid="before-after-container">
    <div className="absolute inset-0">{beforeChild}</div>
    <div
      className="absolute inset-0 overflow-hidden"
      style={{ clipPath: `inset(0 0 0 ${position}%)` }}
    >
      {afterChild}
    </div>
    <div className="relative invisible">{afterChild}</div>
    <div
      className="absolute top-0 bottom-0 w-px bg-[#B8996F] shadow-[0_0_18px_#B8996F]"
      style={{ left: `${position}%` }}
    >
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center bg-[#B8996F] text-[#0A0A0A]">
        <GitCompareArrows size={18} strokeWidth={1.6} />
      </div>
    </div>
    <input
      type="range"
      min={0}
      max={100}
      value={position}
      onChange={(e) => onMove(Number(e.target.value))}
      data-testid="before-after-slider"
      className="absolute inset-x-0 bottom-0 z-10 w-full cursor-ew-resize opacity-0"
      style={{ height: "100%" }}
      aria-label="Before / After slider"
    />
    <div className="pointer-events-none absolute left-3 top-3 bg-black/60 px-2 py-1 text-[10px] uppercase tracking-[0.22em] text-white/85">
      Before
    </div>
    <div className="pointer-events-none absolute right-3 top-3 bg-[#B8996F] px-2 py-1 text-[10px] uppercase tracking-[0.22em] text-[#0A0A0A]">
      After
    </div>
  </div>
);

// ---------- Tab header ----------
const TabSwitch = ({ active, onChange }) => (
  <div className="inline-flex border border-white/10" data-testid="configurator-tabs">
    {[
      { id: "kitchen", label: "Modular Kitchen", Icon: ChefHat },
      { id: "wardrobe", label: "Bedroom Wardrobe", Icon: Shirt },
    ].map(({ id, label, Icon }) => (
      <button
        key={id}
        type="button"
        onClick={() => onChange(id)}
        data-testid={`tab-${id}`}
        className={`group flex items-center gap-2 px-5 py-3 text-[11px] uppercase tracking-[0.22em] transition-colors duration-300 ${
          active === id
            ? "bg-[#F7F5F0] text-[#0A0A0A]"
            : "text-white/65 hover:text-white"
        }`}
      >
        <Icon size={14} strokeWidth={1.6} />
        {label}
      </button>
    ))}
  </div>
);

// ---------- Main Configurator ----------
const Configurator = () => {
  const [activeTab, setActiveTab] = useState("kitchen");
  const [kitchen, setKitchen] = useState(INITIAL_KITCHEN);
  const [wardrobe, setWardrobe] = useState(INITIAL_WARDROBE);
  const [lastKitchenPresetId, setLastKitchenPresetId] = useState(KITCHEN_PRESETS[0].id);
  const [lastWardrobePresetId, setLastWardrobePresetId] = useState(WARDROBE_PRESETS[0].id);
  const [savedDesigns, setSavedDesigns] = useState([]);
  const [showCompare, setShowCompare] = useState(false);
  const [comparePos, setComparePos] = useState(50);
  const [leadOpen, setLeadOpen] = useState(false);
  const [leadContext, setLeadContext] = useState(null);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [postSavedToast, setPostSavedToast] = useState(null);

  // ---- Lead capture pop-up flow ----
  // The pop-up auto-opens after meaningful interaction. If the customer dismisses
  // it without submitting, we snooze it for 2 minutes and re-open it then. Once
  // the customer submits the form successfully, we never auto-open again.
  const [leadSubmitted, setLeadSubmitted] = useState(false);
  const popupShownRef = useRef(false);   // has the popup already been triggered in this session?
  const snoozeTimerRef = useRef(null);

  const openLeadPopup = (context) => {
    popupShownRef.current = true;
    setLeadContext(context);
    setLeadOpen(true);
    // Cancel any pending snooze re-open — we're already showing it
    if (snoozeTimerRef.current) {
      clearTimeout(snoozeTimerRef.current);
      snoozeTimerRef.current = null;
    }
  };

  const closeLeadPopup = () => {
    setLeadOpen(false);
    // If they closed without submitting, schedule a re-open in 2 minutes.
    // (If they submitted, leadSubmitted will be true and we skip.)
    if (snoozeTimerRef.current) clearTimeout(snoozeTimerRef.current);
    snoozeTimerRef.current = setTimeout(() => {
      // re-check submitted state via the closure-captured setter doesn't help —
      // use the functional setState pattern by reading from a ref-style flag instead.
      setLeadSubmitted((submitted) => {
        if (!submitted) {
          setLeadContext({ trigger: "interaction" });
          setLeadOpen(true);
        }
        return submitted;
      });
      snoozeTimerRef.current = null;
    }, 120000); // 2 minutes
  };

  const handleLeadSubmitted = () => {
    setLeadSubmitted(true);
    if (snoozeTimerRef.current) {
      clearTimeout(snoozeTimerRef.current);
      snoozeTimerRef.current = null;
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (snoozeTimerRef.current) clearTimeout(snoozeTimerRef.current);
    };
  }, []);

  // After meaningful interaction (6 changes) auto-open the popup — but only once,
  // and never if the customer has already submitted.
  const [changeCount, setChangeCount] = useState(0);
  useEffect(() => {
    if (changeCount >= 6 && !popupShownRef.current && !leadSubmitted && !leadOpen) {
      openLeadPopup({ trigger: "interaction" });
    }
  }, [changeCount, leadSubmitted, leadOpen]);

  // Load saved designs from localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem("dd_saved_designs");
      if (raw) setSavedDesigns(JSON.parse(raw));
    } catch (err) {
      console.warn("Could not load saved designs", err);
    }
  }, []);

  const persistSaved = (list) => {
    setSavedDesigns(list);
    try {
      localStorage.setItem("dd_saved_designs", JSON.stringify(list));
    } catch (err) {
      console.warn("Could not persist saved designs", err);
    }
  };

  const isKitchen = activeTab === "kitchen";

  const baselineConfig = useMemo(
    () => (isKitchen ? KITCHEN_PRESETS[0].config : WARDROBE_PRESETS[0].config),
    [isKitchen]
  );
  const currentConfig = isKitchen ? kitchen : wardrobe;

  const setKitchenField = (key, value) => {
    setKitchen((c) => ({ ...c, [key]: value }));
    setHasInteracted(true);
    setChangeCount((n) => n + 1);
  };
  const setWardrobeField = (key, value) => {
    setWardrobe((c) => ({ ...c, [key]: value }));
    setHasInteracted(true);
    setChangeCount((n) => n + 1);
  };

  const applyPreset = (preset) => {
    if (isKitchen) {
      setKitchen(preset.config);
      setLastKitchenPresetId(preset.id);
    } else {
      setWardrobe(preset.config);
      setLastWardrobePresetId(preset.id);
    }
    setHasInteracted(true);
    setChangeCount((n) => n + 1);
  };

  // Photo to display: stays on the LAST clicked preset (the user is "fine tuning"
  // a starting style — we don't snap to a different photo when an individual
  // swatch is changed). If the live config still matches one of the presets,
  // we use that match as the source-of-truth.
  const matchedPresetId = useMemo(() => {
    const list = isKitchen ? KITCHEN_PRESETS : WARDROBE_PRESETS;
    return list.find((p) => JSON.stringify(p.config) === JSON.stringify(currentConfig))?.id || null;
  }, [isKitchen, currentConfig]); // eslint-disable-line react-hooks/exhaustive-deps

  const displayedPresetId = isKitchen
    ? (matchedPresetId || lastKitchenPresetId)
    : (matchedPresetId || lastWardrobePresetId);

  // Used for "activePresetId" highlighting in the side panel + save label
  const activePresetId = matchedPresetId;

  const saveDesign = async () => {
    const design = {
      id: `${activeTab}-${Date.now()}`,
      design_type: activeTab,
      config: currentConfig,
      label:
        (isKitchen ? KITCHEN_PRESETS : WARDROBE_PRESETS).find((p) => p.id === activePresetId)?.name ||
        "Custom Design",
      created_at: new Date().toISOString(),
    };
    persistSaved([design, ...savedDesigns].slice(0, 8));
    setPostSavedToast("Design saved to your collection.");
    setTimeout(() => setPostSavedToast(null), 2500);

    // Fire and forget API save
    try {
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      await fetch(`${API}/designs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          design_type: activeTab,
          config: currentConfig,
          label: design.label,
        }),
      });
    } catch (err) {
      console.warn("Design API save failed", err);
    }
  };

  const submitDesign = () => {
    openLeadPopup({
      trigger: "submit",
      design: { design_type: activeTab, config: currentConfig },
    });
  };

  const resetCurrent = () => {
    if (isKitchen) setKitchen(INITIAL_KITCHEN);
    else setWardrobe(INITIAL_WARDROBE);
  };

  return (
    <section
      id="visualize"
      className="relative bg-[#0A0A0A] text-[#F7F5F0]"
      data-testid="interactive-design-space"
    >
      {/* Brass grain texture overlay */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            "radial-gradient(rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "3px 3px",
        }}
      />

      <div className="relative mx-auto max-w-[1400px] px-6 py-20 md:px-12 md:py-32">
        {/* Section header */}
        <div className="grid items-end gap-6 md:grid-cols-12">
          <div className="md:col-span-8">
            <span className="text-[10px] uppercase tracking-[0.32em] text-[#B8996F]">
              02 / Visualization Studio
            </span>
            <h2
              className="mt-4 font-[Cormorant_Garamond] text-4xl font-light leading-[1.02] tracking-tight sm:text-5xl lg:text-6xl"
              data-testid="visualize-title"
            >
              Visualize Your<br />
              <span className="italic text-[#B8996F]">Dream Space</span> Instantly.
            </h2>
          </div>
          <div className="md:col-span-4">
            <p className="font-[Outfit] text-base leading-relaxed text-white/65" data-testid="visualize-subtitle">
              Experiment with colors, finishes and styles in real time to
              discover the look that matches your home.
            </p>
          </div>
        </div>

        {/* Tab Switch */}
        <div className="mt-12 flex flex-wrap items-center justify-between gap-4">
          <TabSwitch active={activeTab} onChange={setActiveTab} />
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setShowCompare((v) => !v)}
              data-testid="toggle-compare-btn"
              className={`flex items-center gap-2 border px-4 py-2 text-[11px] uppercase tracking-[0.22em] transition-colors duration-300 ${
                showCompare
                  ? "border-[#B8996F] bg-[#B8996F] text-[#0A0A0A]"
                  : "border-white/15 text-white/75 hover:border-white/40 hover:text-white"
              }`}
            >
              <GitCompareArrows size={14} strokeWidth={1.6} /> Compare Before / After
            </button>
            <button
              type="button"
              onClick={resetCurrent}
              data-testid="reset-design-btn"
              className="flex items-center gap-2 border border-white/15 px-4 py-2 text-[11px] uppercase tracking-[0.22em] text-white/75 transition-colors duration-300 hover:border-white/40 hover:text-white"
            >
              <RefreshCw size={14} strokeWidth={1.6} /> Reset
            </button>
          </div>
        </div>

        {/* Main canvas + controls */}
        <div className="mt-8 grid gap-8 md:grid-cols-12">
          {/* Canvas */}
          <div className="md:col-span-8">
            <div className="relative border border-white/10 bg-gradient-to-b from-[#181210] via-[#0F0B09] to-[#06050A] p-2 shadow-[0_40px_120px_-30px_rgba(0,0,0,0.9)]">
              {/* Live preview badge */}
              <div className="pointer-events-none absolute left-4 top-4 z-20 flex items-center gap-2 bg-black/55 px-2.5 py-1 text-[9px] uppercase tracking-[0.28em] text-[#B8996F] backdrop-blur">
                <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-[#B8996F]" />
                3D Live Preview
              </div>
              <div className="relative">
                {showCompare ? (
                  <BeforeAfter
                    position={comparePos}
                    onMove={setComparePos}
                    beforeChild={
                      isKitchen ? (
                        <KitchenMockup config={baselineConfig} />
                      ) : (
                        <WardrobeMockup config={baselineConfig} />
                      )
                    }
                    afterChild={
                      isKitchen ? (
                        <KitchenMockup config={kitchen} />
                      ) : (
                        <WardrobeMockup config={wardrobe} />
                      )
                    }
                  />
                ) : (
                  isKitchen ? (
                    <KitchenMockup config={kitchen} />
                  ) : (
                    <WardrobeMockup config={wardrobe} />
                  )
                )}
              </div>

              {/* Action row */}
              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-white/10 px-4 py-4">
                <div className="font-[Outfit] text-xs text-white/55">
                  {hasInteracted ? "Live recolor on a real photograph · materials shown below" : "Tap a preset or swatch — colours apply to the real photo"}
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={saveDesign}
                    data-testid="save-design-btn"
                    className="flex items-center gap-2 border border-white/20 px-4 py-2 text-[11px] uppercase tracking-[0.22em] text-white/85 transition-colors duration-300 hover:border-white hover:text-white"
                  >
                    <Bookmark size={14} strokeWidth={1.6} /> Save Design
                  </button>
                  <button
                    type="button"
                    onClick={submitDesign}
                    data-testid={isKitchen ? "design-my-kitchen-btn" : "design-my-wardrobe-btn"}
                    className="group flex items-center gap-2 bg-[#B8996F] px-5 py-2.5 text-[11px] uppercase tracking-[0.22em] text-[#0A0A0A] transition-colors duration-300 hover:bg-[#F7F5F0]"
                  >
                    <Send size={14} strokeWidth={1.8} />
                    {isKitchen ? "Design My Kitchen" : "Design My Wardrobe"}
                  </button>
                </div>
              </div>
            </div>

            {/* Saved chips */}
            {savedDesigns.length > 0 && (
              <div className="mt-6">
                <div className="mb-2 text-[10px] uppercase tracking-[0.22em] text-white/45">
                  Your Saved Designs
                </div>
                <div className="flex flex-wrap gap-2" data-testid="saved-designs">
                  {savedDesigns.map((d) => (
                    <button
                      key={d.id}
                      type="button"
                      onClick={() => {
                        if (d.design_type === "kitchen") {
                          setActiveTab("kitchen");
                          setKitchen(d.config);
                        } else {
                          setActiveTab("wardrobe");
                          setWardrobe(d.config);
                        }
                      }}
                      data-testid={`saved-design-${d.id}`}
                      className="flex items-center gap-2 border border-white/15 px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] text-white/75 hover:border-white/40 hover:text-white"
                    >
                      <span className="inline-block h-2 w-2 bg-[#B8996F]" />
                      {d.design_type} · {d.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {postSavedToast && (
              <div className="mt-4 border border-[#B8996F]/40 bg-[#B8996F]/10 px-4 py-3 text-xs text-[#F7F5F0]">
                {postSavedToast}
              </div>
            )}
          </div>

          {/* Controls panel */}
          <div className="md:col-span-4">
            <div className="border border-white/10 bg-[#1A1512] p-5">
              <div className="mb-4">
                <div className="text-[10px] uppercase tracking-[0.22em] text-white/45">
                  Preset Styles
                </div>
                <div className="mt-3">
                  <PresetGrid
                    presets={isKitchen ? KITCHEN_PRESETS : WARDROBE_PRESETS}
                    activeId={activePresetId}
                    onPick={applyPreset}
                    idPrefix={isKitchen ? "kitchen" : "wardrobe"}
                  />
                </div>
              </div>

              {isKitchen ? (
                <KitchenControls
                  config={kitchen}
                  swatches={KITCHEN_SWATCHES}
                  onChange={setKitchenField}
                />
              ) : (
                <WardrobeControls
                  config={wardrobe}
                  swatches={WARDROBE_SWATCHES}
                  onChange={setWardrobeField}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <LeadCaptureModal
        open={leadOpen}
        onClose={closeLeadPopup}
        onSubmitted={handleLeadSubmitted}
        context={leadContext}
      />
    </section>
  );
};

export default Configurator;
